import React, { useEffect, useRef, useState } from "react";
import Swiper from "swiper";
import { Link } from "react-router-dom";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import { db } from "../firebase/firebase";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";

// ✅ Import Cart Context
import { useCart } from "../contexts/CartContext";
import { useSale } from "../contexts/SaleContext";
import { getDiscountedPrice, getDiscountBadge, getProductBehaviorType, PRODUCT_BEHAVIOR, getCartItemKey, getEffectiveUnitPrice, getFirstEnabledUnit, getUnitLabel } from "../utils/productUtils";

// ✅ Font Awesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartShopping } from "@fortawesome/free-solid-svg-icons";

function Productslider() {
  const swiperRef = useRef(null);
  const swiperInstance = useRef(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Get addToCart from context
  const { addToCart } = useCart();
  const { sale, discountLabel, categoryDiscounts } = useSale();

  // Discount helpers now come from productUtils

  // Fetch products
  useEffect(() => {
    const q = query(collection(db, "products"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const productList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching slider products:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Swiper
  useEffect(() => {
    if (swiperRef.current) {
      swiperInstance.current = new Swiper(swiperRef.current, {
        modules: [Navigation, Pagination],
        slidesPerView: 3,
        spaceBetween: 30,
        loop: true,
        touchEventsTarget: "container",
        simulateTouch: true,
        allowTouchMove: true,
        navigation: {
          prevEl: ".product-prev",
          nextEl: ".product-next",
        },
        pagination: {
          el: ".product-pagination",
          clickable: true,
        },
        breakpoints: {
          0: { slidesPerView: 2 },
          420: { slidesPerView: 2 },
          768: { slidesPerView: 3 },
          1024: { slidesPerView: 4 },
          1280: { slidesPerView: 5 },
          1601: { slidesPerView: 6 },
          1920: { slidesPerView: 7 },
        },
      });
    }

    return () => {
      if (swiperInstance.current) {
        swiperInstance.current.destroy(true, true);
      }
    };
  }, [products]);

  return (
    <div className="product-section" data-aos="fade-up">
      {/* HEADER */}
      <div className="deals-header">
        <h2 className="deals-title">
          Today Best Deal
          <span className="highlight big-span"> For You!</span>
        </h2>
        <Link to="/products" className="see-all">
          <p>See All</p>
          <svg
            width="15"
            height="13"
            viewBox="0 0 15 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.8223 7.10248C14.1659 6.75882 14.1659 6.20163 13.8223 5.85797L8.22197 0.257686C7.87831 -0.0859759 7.32112 -0.085976 6.97746 0.257686C6.6338 0.601347 6.6338 1.15853 6.97746 1.50219L11.9555 6.48023L6.97746 11.4583C6.6338 11.8019 6.6338 12.3591 6.97746 12.7028C7.32112 13.0464 7.87831 13.0464 8.22197 12.7028L13.8223 7.10248ZM0 6.48022L0 7.36022L13.2 7.36023L13.2 6.48023L13.2 5.60023L0 5.60022L0 6.48022Z"
              fill="#D2222D"
            />
          </svg>
        </Link>
      </div>

      {/* SWIPER */}
      <div className="slider-nav-wrapper">
        <button className="slider-nav-btn product-prev" aria-label="Previous">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M19 13H6.75L12 18.25l-.66.75l-6.5-6.5l6.5-6.5l.66.75L6.75 12H19z" /></svg>
        </button>
        <div className="swiper product-swiper" ref={swiperRef}>
          <div className="swiper-wrapper">
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                <div className="swiper-slide shrink-slide" key={i}>
                  <div className="product-card skeleton-card">
                    <div className="skeleton-image-box skeleton-shimmer" />
                    <div className="skeleton-info">
                      <div className="skeleton-line skeleton-shimmer" style={{ width: "50%", height: "12px" }} />
                      <div className="skeleton-line skeleton-shimmer" style={{ width: "80%", height: "16px", marginTop: "8px" }} />
                      <div className="skeleton-line skeleton-shimmer" style={{ width: "40%", height: "14px", marginTop: "10px" }} />
                    </div>
                  </div>
                </div>
              ))
              : products.map((product) => (
                <div className="swiper-slide shrink-slide" key={product.id}>
                  <Link
                    to={`/product/${product.id}`}
                    className="product-card-link"
                    style={{ textDecoration: "none", display: "flex", width: "100%", height: "100%" }}
                  >
                    <div className="product-card">
                      <div className="card-image-box">
                        {getDiscountBadge(product, sale, discountLabel, categoryDiscounts) && (
                          <span className="discount-badge">{getDiscountBadge(product, sale, discountLabel, categoryDiscounts)}</span>
                        )}
                        {product.isOutOfStock && (
                          <span className="oos-overlay-badge">Out of Stock</span>
                        )}

                        <img src={product.picture} alt={product.name} />

                        {!product.isOutOfStock && (
                          <div className="add-to-cart-btn-wrapper">
                            <button
                              className="add-cart-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                const isMed     = getProductBehaviorType(product.type) === PRODUCT_BEHAVIOR.MEDICINE;
                                const firstUnit = isMed ? getFirstEnabledUnit(product) : null;
                                const cartKey   = getCartItemKey(product.id, product.type, firstUnit);
                                const unitLabel = isMed ? ` (${getUnitLabel(product, firstUnit)})` : "";
                                const unitPrice = getEffectiveUnitPrice(product, sale, discountLabel, firstUnit, categoryDiscounts);
                                addToCart({ ...product, id: cartKey, productId: product.id, name: product.name + unitLabel, selectedUnit: firstUnit, price: unitPrice });
                              }}
                              title="Add to cart"
                            >
                              <FontAwesomeIcon icon={faCartShopping} />
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="card-info">
                        <h3 className="product-type">{product.type}</h3>
                        <h4 className="product-name">{product.name}</h4>

                        <div className="price-container">
                          {(() => {
                            const isMed     = getProductBehaviorType(product.type) === PRODUCT_BEHAVIOR.MEDICINE;
                            const firstUnit = isMed ? getFirstEnabledUnit(product) : null;
                            const discounted = getDiscountedPrice(product, sale, discountLabel, firstUnit, categoryDiscounts);
                            const basePrice  = isMed
                              ? (product.sellingOptions?.[firstUnit]?.price ?? product.price)
                              : (product.retailPrice || product.price);
                            return discounted ? (
                              <>
                                <span className="new-price">Rs. {discounted}</span>
                                <span className="old-price">Rs. {basePrice}</span>
                              </>
                            ) : (
                              <span className="new-price">Rs. {basePrice}</span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))
            }
          </div>
        </div>
        <button className="slider-nav-btn product-next" aria-label="Next">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M4 12h12.25L11 6.75l.66-.75l6.5 6.5l-6.5 6.5l-.66-.75L16.25 13H4z" /></svg>
        </button>
      </div>
    </div>
  );
}

export default Productslider;